"use client";

import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import Image from "next/image";
import EditableElement from "@/components/customize/EditableElement";

export default function Hero({
  images = [],
  interval = 3000,
  heading,
  date,
  dateFormat = "long",
  description,
  showChevron = true,
  templateMeta,
  __isMobile = false,
  __isEditable = false,
  __sectionId,
  __onFieldEdit,
  __onFieldChange,
  __focusFieldName,
}) {
  const safeImages = (images || []).filter(
    (img) => typeof img === "string" && img.trim() !== ""
  );
  const [current, setCurrent] = useState(0);

  const formatDateDisplay = (dateString, formatType) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;

    const dayNameEn = d.toLocaleDateString("en-US", { weekday: "long" });
    const monthLongEn = d.toLocaleDateString("en-US", { month: "long" });
    const monthShortEn = d.toLocaleDateString("en-US", { month: "short" });

    const dayNameId = d.toLocaleDateString("id-ID", { weekday: "long" });
    const monthLongId = d.toLocaleDateString("id-ID", { month: "long" });
    const monthShortId = d.toLocaleDateString("id-ID", { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear();

    const getSuffix = (n) => {
      if (n >= 11 && n <= 13) return "th";
      switch (n % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };

    switch (formatType) {
      case "short":
        return `${monthShortEn} ${day}, ${year}`;
      case "numeric":
        return `${String(day).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${year}`;
      case "full":
        return `${dayNameEn}, ${monthLongEn} ${day}${getSuffix(day)}, ${year}`;
      case "id-long":
        return `${dayNameId}, ${day} ${monthLongId} ${year}`;
      case "id-short":
        return `${day} ${monthShortId} ${year}`;
      case "long":
      default:
        return `${dayNameEn}, ${day} ${monthLongEn} ${year}`;
    }
  };

  const displayDate = formatDateDisplay(templateMeta?.marriageContractDate || templateMeta?.receptionDate || date, dateFormat);
  const headingSizeClass = __isMobile ? "text-2xl" : "text-2xl md:text-3xl";
  const dateSizeClass = __isMobile ? "text-xl" : "text-xl md:text-2xl";
  const contentPaddingClass = __isMobile ? "px-5" : "px-5 md:px-12";
  const descMaxWidthClass = __isMobile ? "max-w-sm" : "max-w-xl md:max-w-md";
  const safeCurrent = safeImages.length ? current % safeImages.length : 0;
  
  const brideAndGroom = `${templateMeta?.groomFirstName || "Groom"} & ${templateMeta?.brideFirstName || "Bride"}`;

  useEffect(() => {
    if (!safeImages.length) return;
    const intervalId = setInterval(() => {
      setCurrent((prevIndex) => {
        return (prevIndex + 1) % safeImages.length;
      });
    }, interval);
    return () => clearInterval(intervalId);
  }, [safeImages.length, interval]);

  if (!safeImages.length) return null;

  return (
    <div className="relative h-(--frame-height,100vh) overflow-hidden bg-black">
      {/* Background Images */}
      {safeImages.map((img, index) => (
        <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: index === safeCurrent ? 1 : 0 }}
            transition={{ duration: 1, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full z-0"
        >
          <Image 
            src={img} 
            alt={`Background ${index}`} 
            fill 
            className="object-cover" 
            sizes="(max-width: 1024px) 100vw, 50vw" 
            priority={index === 0} 
          />
        </motion.div>
      ))}

      {/* Foreground Content */}
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-white text-center px-8">
        {heading && (
          <EditableElement
            type="text"
            value={heading}
            onChange={(val) => __onFieldChange?.(__sectionId, "heading", val)}
            isEditable={__isEditable}
            fieldName="heading"
            onEditStart={() => __onFieldEdit?.(__sectionId, "heading")}
            isActive={__focusFieldName === "heading"}
            isMobile={__isMobile}
          >
            <p className="font-caudex italic text-xs">{heading}</p>
          </EditableElement>
        )}
        <EditableElement
          type="text"
          value={brideAndGroom}
          isEditable={__isEditable}
          fieldName="heroBrideGroom"
          onEditStart={() => __onFieldEdit?.("general", "groomFirstName")}
          useContentEditable={false}
        >
          <p className={`font-cormorant font-bold leading-tight uppercase tracking-widest ${headingSizeClass}`}>
            {brideAndGroom}
          </p>
        </EditableElement>
        <div className={contentPaddingClass}>
          {displayDate && (
            <div className="mt-24 mb-2">
              <EditableElement
                type="text"
                value={displayDate}
                isEditable={__isEditable}
                fieldName="heroDate"
                onEditStart={() => __onFieldEdit?.("general", "marriageContractDate")}
                useContentEditable={false}
              >
                <p className={`font-caudex uppercase ${dateSizeClass}`}>
                  {displayDate}
                </p>
              </EditableElement>
            </div>
          )}
          {description && (
            <EditableElement
              type="text"
              value={description}
              onChange={(val) => __onFieldChange?.(__sectionId, "description", val)}
              isEditable={__isEditable}
              fieldName="description"
              onEditStart={() => __onFieldEdit?.(__sectionId, "description")}
              isActive={__focusFieldName === "description"}
              isMobile={__isMobile}
              useContentEditable={true}
              multiline={true}
            >
              <p className={`font-sans text-xs text-center leading-relaxed ${descMaxWidthClass}`}>
                {description}
              </p>
            </EditableElement>
          )}
        </div>
      </div>

      {/* Chevron Icon */}
      {showChevron && (
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
          animate={{ y: [0, -10, 0] }}
          transition={{
            repeat: Infinity,
            duration: 2,
            ease: "easeInOut",
          }}
          style={{ willChange: "transform" }}
        >
          <ChevronDown className="w-6 h-6 text-white" />
        </motion.div>
      )}
    </div>
  );
}
